import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { onGetLogsAsync } from './PensionLogs.slice'
import Spinner from '../../components/Spinners/Spinner'
import CustomTable from '../../components/Tables/CustomTable'
import LogMetadata from './LogMetadata'
import LogDetailDrawer from './LogDetailDrawer'
import {
  Container,
  CountLabel,
  EmptyState,
  ErrorText,
  FilterButton,
  FilterGroup,
  LevelBadge,
  ListToolbar,
  MessageCell,
  TimeCell,
} from './PensionLogs.styles'

const LEVEL_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'error', label: 'Error' },
]

const isErrorLevel = (level) => {
  const normalized = String(level || '').toLowerCase()
  return normalized === 'error' || normalized === 'fatal'
}

const PensionLogsList = ({ context }) => {
  const { logsByContext, loadingByContext, errorByContext } = useSelector(
    (state) => state.adminPensionLogs
  )
  const [levelFilter, setLevelFilter] = useState('all')
  const [selectedLog, setSelectedLog] = useState(null)

  const logs = useMemo(
    () => logsByContext[context] || [],
    [logsByContext, context]
  )
  const loading = loadingByContext[context] || false
  const error = errorByContext[context] || null

  const dispatch = useDispatch()

  const visibleLogs = useMemo(() => {
    if (levelFilter === 'error') {
      return logs.filter((log) => isErrorLevel(log.level))
    }
    return logs
  }, [logs, levelFilter])

  const errorCount = useMemo(
    () => logs.filter((log) => isErrorLevel(log.level)).length,
    [logs]
  )

  const columns = [
    {
      key: 'createdAt',
      label: 'Time',
      render: (value) => <TimeCell>{value}</TimeCell>,
    },
    {
      key: 'level',
      label: 'Lvl',
      render: (level) => (
        <LevelBadge $tone={isErrorLevel(level) ? 'error' : 'info'}>
          {level || '—'}
        </LevelBadge>
      ),
    },
    {
      key: 'message',
      label: 'Message',
      render: (message) => (
        <MessageCell title={message || ''}>{message}</MessageCell>
      ),
    },
    {
      key: 'metadata',
      label: 'Metadata',
      render: (metadata) => <LogMetadata metadata={metadata} context={context} />,
    },
  ]

  useEffect(() => {
    setLevelFilter('all')
    setSelectedLog(null)
    dispatch(onGetLogsAsync({ context }))
  }, [context, dispatch])

  return (
    <Container>
      <ListToolbar>
        <FilterGroup role="group" aria-label="Filtrar por nivel">
          {LEVEL_FILTERS.map(({ key, label }) => (
            <FilterButton
              key={key}
              type="button"
              $active={levelFilter === key}
              onClick={() => setLevelFilter(key)}
            >
              {label}
              {key === 'error' ? ` (${errorCount})` : ''}
            </FilterButton>
          ))}
        </FilterGroup>
        <CountLabel>
          {visibleLogs.length} / {logs.length} logs
        </CountLabel>
      </ListToolbar>

      {loading && <Spinner />}
      {!loading && error && (
        <ErrorText>No se pudieron cargar los logs. Intenta de nuevo.</ErrorText>
      )}
      {!loading && !error && visibleLogs.length === 0 && (
        <EmptyState>
          <strong>
            {logs.length === 0
              ? `Sin logs · ${context === 'SENDER' ? 'Sender' : 'Scraper'}`
              : 'Sin errores en esta carga'}
          </strong>
          <span>
            {logs.length === 0
              ? 'Cuando el proceso se ejecute, aparecerán aquí.'
              : 'Cambia el filtro a Todos para ver info.'}
          </span>
        </EmptyState>
      )}
      {!loading && !error && visibleLogs.length > 0 && (
        <CustomTable
          columns={columns}
          data={visibleLogs}
          showIndex
          compact
          onRowClick={(row) => setSelectedLog(row)}
          getRowProps={(row) => ({
            $danger: isErrorLevel(row.level),
            $selected: selectedLog?.id != null && selectedLog.id === row.id,
          })}
        />
      )}

      <LogDetailDrawer
        log={selectedLog}
        context={context}
        onClose={() => setSelectedLog(null)}
      />
    </Container>
  )
}

export default PensionLogsList
